import React, { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { AuthContext } from '../App';
import PremiumGuard from './PremiumGuard';

// Helper function to calculate age
const calculateAge = (dobDate) => {
    if (!dobDate) return 'N/A';
    const diff = Date.now() - dobDate.getTime();
    const ageDate = new Date(diff);
    return Math.abs(ageDate.getUTCFullYear() - 1970);
};

// Helper function to format time
const formatTime = (timeString) => {
    if (!timeString) return '';
    try {
        const time = new Date(`2000-01-01T${timeString}`);
        return time.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } catch {
        return timeString;
    }
};

export default function ProfileView() {
    const { userId } = useParams();
    const navigate = useNavigate();
    const { db, isAuthReady } = useContext(AuthContext);
    const [profile, setProfile] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    useEffect(() => {
        const fetchProfile = async () => {
            if (!isAuthReady || !userId || !db) {
                setLoading(false);
                return;
            }

            try {
                let profileDocRef = doc(db, "users", userId);
                let profileDocSnap = await getDoc(profileDocRef);
                
                if (!profileDocSnap.exists()) {
                    profileDocRef = doc(db, "profiles", userId);
                    profileDocSnap = await getDoc(profileDocRef);
                }
                
                if (profileDocSnap.exists()) {
                    const data = profileDocSnap.data();
                    setProfile({
                        id: profileDocSnap.id,
                        firstName: data.firstName || '',
                        lastName: data.lastName || '',
                        gender: data.gender || '',
                        email: data.email || '',
                        phoneNumber: data.phoneNumber || '',
                        dob: data.dob && data.dob.toDate ? data.dob.toDate() : 
                             data.dateOfBirth ? new Date(data.dateOfBirth) : null,
                        profileCreatedBy: data.profileCreatedBy || '',
                        maritalStatus: data.maritalStatus || '',
                        motherTongue: data.motherTongue || '',
                        church: data.church || '',
                        pastorName: data.pastorName || '',
                        photo: data.profilePhotoUrl || data.photo || '',
                        galleryPhotos: data.galleryPhotoUrls || [],
                        country: data.country || '',
                        state: data.state || '',
                        city: data.city || '',
                        postalCode: data.postalCode || '',
                        height: data.height || '',
                        weight: data.weight || '',
                        bodyType: data.bodyType || '',
                        bodyShape: data.bodyShape || '',
                        hairColor: data.hairColor || '',
                        eyeColor: data.eyeColor || '',
                        skinColor: data.skinColor || '',
                        bodyArt: data.bodyArt || '',
                        starNakshatra: data.starNakshatra || '',
                        rasiZodiac: data.rasiZodiac || '',
                        timeOfBirth: data.timeOfBirth || '',
                        placeOfBirth: data.placeOfBirth || '',
                        degree: data.degree || '',
                        institution: data.institution || '',
                        educationStatus: data.educationStatus || '',
                        occupation: data.occupation || '',
                        designation: data.designation || '',
                        company: data.company || '',
                        salary: data.salary || '',
                        religion: data.religion || '',
                        caste: data.caste || '',
                        politicalView: data.politicalView || '',
                        hobbies: data.hobbies || '',
                        favoriteMusic: data.favoriteMusic || '',
                        favoriteBooks: data.favoriteBooks || '',
                        favoriteSports: data.favoriteSports || '',
                        profileSummary: data.profileSummary || data.bio || ''
                    });
                } else {
                    setError('Profile not found');
                }
            } catch (error) {
                console.error("Error fetching profile:", error);
                setError(`Error loading profile: ${error.message}`);
            } finally {
                setLoading(false);
            }
        };

        fetchProfile();
    }, [userId, isAuthReady, db]);

    const sectionClasses = "bg-white p-8 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 mb-6";
    const labelClasses = "block text-sm font-semibold mb-2";

    if (loading) {
        return (
            <div className="container mx-auto px-6 py-8 bg-gray-50 mt-20">
                <div className="max-w-6xl mx-auto pt-8">
                    <div className="text-center">
                        <h2 className="text-4xl font-bold mb-6" style={{ color: 'hsl(221, 45%, 18%)' }}>Loading Profile...</h2>
                        <div className="animate-pulse">
                            <div className="h-64 w-64 bg-gray-200 rounded-full mx-auto mb-4"></div>
                            <div className="h-6 bg-gray-200 rounded mb-2 w-1/3 mx-auto"></div>
                            <div className="h-4 bg-gray-200 rounded mb-4 w-1/4 mx-auto"></div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    if (error || !profile) {
        return (
            <div className="container mx-auto px-6 py-8 bg-gray-50 mt-20">
                <div className="max-w-6xl mx-auto pt-8">
                    <div className="text-center">
                        <h2 className="text-4xl font-bold mb-6" style={{ color: 'hsl(221, 45%, 18%)' }}>Error</h2>
                        <p className="text-lg text-gray-700 mb-4">{error || 'Profile not found'}</p>
                        <button
                            onClick={() => navigate(-1)}
                            className="px-8 py-3 bg-white border-2 rounded-xl font-bold shadow-lg hover:shadow-xl transition-all duration-300"
                            style={{ color: 'hsl(221, 45%, 18%)', borderColor: 'hsl(221, 45%, 18%)' }}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.transform = 'translateY(-2px)';
                                e.currentTarget.style.backgroundColor = 'hsl(221, 45%, 18%)';
                                e.currentTarget.style.color = 'white';
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.transform = 'translateY(0)';
                                e.currentTarget.style.backgroundColor = 'white';
                                e.currentTarget.style.color = 'hsl(221, 45%, 18%)';
                            }}
                        >
                            Go Back
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="container mx-auto px-6 py-8 bg-gray-50 mt-20">
            <div className="max-w-6xl mx-auto pt-8">
                {/* Header */}
                <div className="text-center mb-8">
                    <div className="flex items-center justify-between mb-6">
                        <button
                            onClick={() => navigate(-1)}
                            className="px-6 py-3 bg-white border-2 rounded-xl font-bold shadow-lg hover:shadow-xl transition-all duration-300"
                            style={{ color: 'hsl(221, 45%, 18%)', borderColor: 'hsl(221, 45%, 18%)' }}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.transform = 'translateY(-2px)';
                                e.currentTarget.style.backgroundColor = 'hsl(221, 45%, 18%)';
                                e.currentTarget.style.color = 'white';
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.transform = 'translateY(0)';
                                e.currentTarget.style.backgroundColor = 'white';
                                e.currentTarget.style.color = 'hsl(221, 45%, 18%)';
                            }}
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 inline mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                            </svg>
                            Back
                        </button>
                        <h1 className="text-5xl font-bold" style={{ color: 'hsl(221, 45%, 18%)' }}>Profile Details</h1>
                        <div className="w-24"></div>
                    </div>
                    <p className="text-lg text-gray-600">View complete profile information</p>
                </div>

                {/* Profile Header Card */}
                <div className={sectionClasses}>
                    <div className="flex flex-col md:flex-row items-center space-y-6 md:space-y-0 md:space-x-8">
                        <div className="w-32 h-32 rounded-full overflow-hidden border-4 shadow-lg" style={{ borderColor: 'hsl(43, 74%, 52%)' }}>
                            <img
                                src={profile.photo || "https://placehold.co/128x128/E0E7FF/4F46E5?text=No+Photo"}
                                alt={`${profile.firstName} ${profile.lastName}`}
                                className="w-full h-full object-cover"
                                onError={(e) => { 
                                    e.target.onerror = null; 
                                    e.target.src = "https://placehold.co/128x128/E0E7FF/4F46E5?text=No+Photo"; 
                                }}
                            />
                        </div>
                        <div className="text-center md:text-left">
                            <h2 className="text-4xl font-bold mb-2" style={{ color: 'hsl(221, 45%, 18%)' }}>
                                {profile.firstName} {profile.lastName}
                            </h2>
                            <div className="text-xl text-gray-700 mb-2">
                                {profile.gender && <span>{profile.gender}, </span>}
                                <span>{calculateAge(profile.dob)} years old</span>
                            </div>
                            <div className="text-lg text-gray-600">
                                {profile.city}{profile.state && `, ${profile.state}`}{profile.country && `, ${profile.country}`}
                            </div>
                            {profile.profileCreatedBy && (
                                <div className="text-md text-gray-600 mt-2">
                                    Profile created by: {profile.profileCreatedBy}
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* Gallery Photos */}
                {profile.galleryPhotos && profile.galleryPhotos.length > 0 && (
                    <div className={sectionClasses}>
                        <div className="flex items-center mb-6 pb-4 border-b-2" style={{ borderColor: 'hsl(43, 74%, 52%)' }}>
                            <div className="w-12 h-12 rounded-xl flex items-center justify-center mr-4" 
                                 style={{ background: 'linear-gradient(135deg, hsl(43, 74%, 52%, 0.15) 0%, hsl(43, 84%, 62%, 0.15) 100%)' }}>
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" style={{ color: 'hsl(43, 74%, 52%)' }} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                </svg>
                            </div>
                            <h3 className="text-2xl font-bold" style={{ color: 'hsl(221, 45%, 18%)' }}>Photo Gallery</h3>
                        </div>
                        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
                            {profile.galleryPhotos.map((photoUrl, index) => (
                                <div key={index} className="relative group">
                                    <img
                                        src={photoUrl}
                                        alt={`Gallery Photo ${index + 1}`}
                                        className="w-full h-32 object-cover rounded-xl shadow-md hover:shadow-lg transition-all duration-300 cursor-pointer"
                                        onClick={() => window.open(photoUrl, '_blank')}
                                        onError={(e) => { 
                                            e.target.onerror = null; 
                                            e.target.src = "https://placehold.co/128x128/E0E7FF/4F46E5?text=Error"; 
                                        }}
                                    />
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* Basic & Contact Information */}
                <div className={sectionClasses}>
                    <div className="flex items-center mb-6 pb-4 border-b-2" style={{ borderColor: 'hsl(43, 74%, 52%)' }}>
                        <div className="w-12 h-12 rounded-xl flex items-center justify-center mr-4" 
                             style={{ background: 'linear-gradient(135deg, hsl(43, 74%, 52%, 0.15) 0%, hsl(43, 84%, 62%, 0.15) 100%)' }}>
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" style={{ color: 'hsl(43, 74%, 52%)' }} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                            </svg>
                        </div>
                        <h3 className="text-2xl font-bold" style={{ color: 'hsl(221, 45%, 18%)' }}>Basic & Contact Information</h3>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <PremiumGuard>
                            <div className="contact-info p-4 border rounded-lg mt-4 md:col-span-2">
                                <h4 className="font-semibold mb-2" style={{ color: 'hsl(221, 45%, 18%)' }}>Contact Information</h4>
                                {profile.phoneNumber && (
                                    <p className="mb-1"><span className="font-medium">Phone:</span> {profile.phoneNumber}</p>
                                )}
                                {profile.email && (
                                    <p className="mb-1"><span className="font-medium">Email:</span> {profile.email}</p>
                                )}
                            </div>
                        </PremiumGuard>

                        {profile.maritalStatus && (
                            <div>
                                <label className={labelClasses} style={{ color: 'hsl(221, 45%, 18%)' }}>Marital Status</label>
                                <p className="text-lg text-gray-800">{profile.maritalStatus}</p>
                            </div>
                        )}
                        {profile.motherTongue && (
                            <div>
                                <label className={labelClasses} style={{ color: 'hsl(221, 45%, 18%)' }}>Mother Tongue</label>
                                <p className="text-lg text-gray-800">{profile.motherTongue}</p>
                            </div>
                        )}
                        {profile.religion && (
                            <div>
                                <label className={labelClasses} style={{ color: 'hsl(221, 45%, 18%)' }}>Religion</label>
                                <p className="text-lg text-gray-800">{profile.religion}</p>
                            </div>
                        )}
                        {profile.caste && (
                            <div>
                                <label className={labelClasses} style={{ color: 'hsl(221, 45%, 18%)' }}>Caste</label>
                                <p className="text-lg text-gray-800">{profile.caste}</p>
                            </div>
                        )}
                        {profile.church && (
                            <div>
                                <label className={labelClasses} style={{ color: 'hsl(221, 45%, 18%)' }}>Church</label>
                                <p className="text-lg text-gray-800">{profile.church}</p>
                            </div>
                        )}
                        {profile.pastorName && (
                            <div>
                                <label className={labelClasses} style={{ color: 'hsl(221, 45%, 18%)' }}>Pastor Name</label>
                                <p className="text-lg text-gray-800">{profile.pastorName}</p>
                            </div>
                        )}
                        {profile.country && (
    <div>
        <label className={labelClasses} style={{ color: 'hsl(221, 45%, 18%)' }}>Country</label>
        <p className="text-lg text-gray-800">{profile.country}</p>
    </div>
)}
{profile.state && (
    <div>
        <label className={labelClasses} style={{ color: 'hsl(221, 45%, 18%)' }}>State</label>
        <p className="text-lg text-gray-800">{profile.state}</p>
    </div>
)}
                    </div>
                </div>

                

                {/* Appearance & Horoscope */}
                <div className={sectionClasses}>
                    <div className="flex items-center mb-6 pb-4 border-b-2" style={{ borderColor: 'hsl(43, 74%, 52%)' }}>
                        <div className="w-12 h-12 rounded-xl flex items-center justify-center mr-4" 
                             style={{ background: 'linear-gradient(135deg, hsl(43, 74%, 52%, 0.15) 0%, hsl(43, 84%, 62%, 0.15) 100%)' }}>
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" style={{ color: 'hsl(43, 74%, 52%)' }} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M5.121 17.804A13.937 13.937 0 0112 16c2.5 0 4.847.655 6.879 1.804M15 10a3 3 0 11-6 0 3 3 0 016 0zm6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                        </div>
                        <h3 className="text-2xl font-bold" style={{ color: 'hsl(221, 45%, 18%)' }}>Physical Appearance</h3>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {profile.height && (
                            <div>
                                <label className={labelClasses} style={{ color: 'hsl(221, 45%, 18%)' }}>Height</label>
                                <p className="text-lg text-gray-800">{profile.height} cm</p>
                            </div>
                        )}
                        {profile.weight && (
                            <div>
                                <label className={labelClasses} style={{ color: 'hsl(221, 45%, 18%)' }}>Weight</label>
                                <p className="text-lg text-gray-800">{profile.weight} kg</p>
                            </div>
                        )}
                        {profile.bodyType && (
                            <div>
                                <label className={labelClasses} style={{ color: 'hsl(221, 45%, 18%)' }}>Body Type</label>
                                <p className="text-lg text-gray-800">{profile.bodyType}</p>
                            </div>
                        )}
                        {profile.bodyShape && (
                            <div>
                                <label className={labelClasses} style={{ color: 'hsl(221, 45%, 18%)' }}>Body Shape</label>
                                <p className="text-lg text-gray-800">{profile.bodyShape}</p>
                            </div>
                        )}
                        {profile.skinColor && (
                            <div>
                                <label className={labelClasses} style={{ color: 'hsl(221, 45%, 18%)' }}>Skin Color</label>
                                <p className="text-lg text-gray-800">{profile.skinColor}</p>
                            </div>
                        )}
                        {profile.hairColor && (
                            <div>
                                <label className={labelClasses} style={{ color: 'hsl(221, 45%, 18%)' }}>Hair Color</label>
                                <p className="text-lg text-gray-800">{profile.hairColor}</p>
                            </div>
                        )}
                        {profile.eyeColor && (
                            <div>
                                <label className={labelClasses} style={{ color: 'hsl(221, 45%, 18%)' }}>Eye Color</label>
                                <p className="text-lg text-gray-800">{profile.eyeColor}</p>
                            </div>
                        )}
                        {profile.bodyArt && (
                            <div>
                                <label className={labelClasses} style={{ color: 'hsl(221, 45%, 18%)' }}>Body Art</label>
                                <p className="text-lg text-gray-800">{profile.bodyArt}</p>
                            </div>
                        )}
                    </div>

                    {(profile.starNakshatra || profile.rasiZodiac || profile.timeOfBirth || profile.placeOfBirth) && (
                        <div className="mt-8 pt-6 border-t-2" style={{ borderColor: 'hsl(43, 74%, 52%, 0.3)' }}>
                            <h4 className="text-xl font-bold mb-4" style={{ color: 'hsl(221, 45%, 18%)' }}>Astrology / Horoscope</h4>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                {profile.starNakshatra && (
                                    <div>
                                        <label className={labelClasses} style={{ color: 'hsl(221, 45%, 18%)' }}>Star (Nakshatra)</label>
                                        <p className="text-lg text-gray-800">{profile.starNakshatra}</p>
                                    </div>
                                )}
                                {profile.rasiZodiac && (
                                    <div>
                                        <label className={labelClasses} style={{ color: 'hsl(221, 45%, 18%)' }}>Rasi (Zodiac)</label>
                                        <p className="text-lg text-gray-800">{profile.rasiZodiac}</p>
                                    </div>
                                )}
                                {profile.timeOfBirth && (
                                    <div>
                                        <label className={labelClasses} style={{ color: 'hsl(221, 45%, 18%)' }}>Time of Birth</label>
                                        <p className="text-lg text-gray-800">{formatTime(profile.timeOfBirth)}</p>
                                    </div>
                                )}
                                {profile.placeOfBirth && (
                                    <div>
                                        <label className={labelClasses} style={{ color: 'hsl(221, 45%, 18%)' }}>Place of Birth</label>
                                        <p className="text-lg text-gray-800">{profile.placeOfBirth}</p>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </div>

                {/* Education & Career */}
                <div className={sectionClasses}>
                    <div className="flex items-center mb-6 pb-4 border-b-2" style={{ borderColor: 'hsl(43, 74%, 52%)' }}>
                        <div className="w-12 h-12 rounded-xl flex items-center justify-center mr-4" 
                             style={{ background: 'linear-gradient(135deg, hsl(43, 74%, 52%, 0.15) 0%, hsl(43, 84%, 62%, 0.15) 100%)' }}>
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" style={{ color: 'hsl(43, 74%, 52%)' }} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                                <path d="M12 14l9-5-9-5-9 5 9 5z" />
                                <path d="M12 14l6.16-3.422a12.083 12.083 0 01.665 6.479A11.952 11.952 0 0012 20.055a11.952 11.952 0 00-6.824-2.998 12.078 12.078 0 01.665-6.479L12 14z" />
                                <path strokeLinecap="round" strokeLinejoin="round" d="M12 14l9-5-9-5-9 5 9 5zm0 0l6.16-3.422a12.083 12.083 0 01.665 6.479A11.952 11.952 0 0012 20.055a11.952 11.952 0 00-6.824-2.998 12.078 12.078 0 01.665-6.479L12 14zm-4 6v-7.5l4-2.222" />
                            </svg>
                        </div>
                        <h3 className="text-2xl font-bold" style={{ color: 'hsl(221, 45%, 18%)' }}>Education & Career</h3>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {profile.degree && (
                            <div>
                                <label className={labelClasses} style={{ color: 'hsl(221, 45%, 18%)' }}>Degree</label>
                                <p className="text-lg text-gray-800">{profile.degree}</p>
                            </div>
                        )}
                        {profile.institution && (
                            <div>
                                <label className={labelClasses} style={{ color: 'hsl(221, 45%, 18%)' }}>Institution</label>
                                <p className="text-lg text-gray-800">{profile.institution}</p>
                            </div>
                        )}
                        {profile.educationStatus && (
                            <div>
                                <label className={labelClasses} style={{ color: 'hsl(221, 45%, 18%)' }}>Education Status</label>
                                <p className="text-lg text-gray-800">{profile.educationStatus}</p>
                            </div>
                        )}
                        {profile.occupation && (
                            <div>
                                <label className={labelClasses} style={{ color: 'hsl(221, 45%, 18%)' }}>Occupation</label>
                                <p className="text-lg text-gray-800">{profile.occupation}</p>
                            </div>
                        )}
                        {profile.designation && (
                            <div>
                                <label className={labelClasses} style={{ color: 'hsl(221, 45%, 18%)' }}>Designation</label>
                                <p className="text-lg text-gray-800">{profile.designation}</p>
                            </div>
                        )}
                        {profile.company && (
                            <div>
                                <label className={labelClasses} style={{ color: 'hsl(221, 45%, 18%)' }}>Company</label>
                                <p className="text-lg text-gray-800">{profile.company}</p>
                            </div>
                        )}
                        {profile.salary && (
                            <div>
                                <label className={labelClasses} style={{ color: 'hsl(221, 45%, 18%)' }}>Annual Salary</label>
                                <p className="text-lg text-gray-800">{profile.salary}</p>
                            </div>
                        )}
                    </div>
                </div>

                {/* Lifestyle & Interests */}
                {(profile.hobbies || profile.favoriteMusic || profile.favoriteBooks || profile.favoriteSports || profile.politicalView) && (
                    <div className={sectionClasses}>
                        <div className="flex items-center mb-6 pb-4 border-b-2" style={{ borderColor: 'hsl(43, 74%, 52%)' }}>
                            <div className="w-12 h-12 rounded-xl flex items-center justify-center mr-4" 
                                 style={{ background: 'linear-gradient(135deg, hsl(43, 74%, 52%, 0.15) 0%, hsl(43, 84%, 62%, 0.15) 100%)' }}>
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" style={{ color: 'hsl(43, 74%, 52%)' }} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M14.828 14.828a4 4 0 01-5.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                            </div>
                            <h3 className="text-2xl font-bold" style={{ color: 'hsl(221, 45%, 18%)' }}>Interests & Lifestyle</h3>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            {profile.hobbies && (
                                <div>
                                    <label className={labelClasses} style={{ color: 'hsl(221, 45%, 18%)' }}>Hobbies</label>
                                    <p className="text-lg text-gray-800">{profile.hobbies}</p>
                                </div>
                            )}
                            {profile.favoriteMusic && (
                                <div>
                                    <label className={labelClasses} style={{ color: 'hsl(221, 45%, 18%)' }}>Favorite Music</label>
                                    <p className="text-lg text-gray-800">{profile.favoriteMusic}</p>
                                </div>
                            )}
                            {profile.favoriteBooks && (
                                <div>
                                    <label className={labelClasses} style={{ color: 'hsl(221, 45%, 18%)' }}>Favorite Books</label>
                                    <p className="text-lg text-gray-800">{profile.favoriteBooks}</p>
                                </div>
                            )}
                            {profile.favoriteSports && (
                                <div>
                                    <label className={labelClasses} style={{ color: 'hsl(221, 45%, 18%)' }}>Favorite Sports</label>
                                    <p className="text-lg text-gray-800">{profile.favoriteSports}</p>
                                </div>
                            )}
                            {profile.politicalView && (
                                <div>
                                    <label className={labelClasses} style={{ color: 'hsl(221, 45%, 18%)' }}>Political View</label>
                                    <p className="text-lg text-gray-800">{profile.politicalView}</p>
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {/* Profile Summary */}
                {profile.profileSummary && (
                    <div className={sectionClasses}>
                        <div className="flex items-center mb-6 pb-4 border-b-2" style={{ borderColor: 'hsl(43, 74%, 52%)' }}>
                            <div className="w-12 h-12 rounded-xl flex items-center justify-center mr-4" 
                                 style={{ background: 'linear-gradient(135deg, hsl(43, 74%, 52%, 0.15) 0%, hsl(43, 84%, 62%, 0.15) 100%)' }}>
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" style={{ color: 'hsl(43, 74%, 52%)' }} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                </svg>
                            </div>
                            <h3 className="text-2xl font-bold" style={{ color: 'hsl(221, 45%, 18%)' }}>About Me</h3>
                        </div>
                        <p className="text-gray-700 leading-relaxed text-lg bg-gray-50 p-6 rounded-xl">
                            {profile.profileSummary}
                        </p>
                    </div>
                )}
            </div>
        </div>
    );
}